/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useContext,
  useMemo,
  ReactNode,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "./useLocalStorage";
import { useDispatch } from "react-redux";
import { isTokenValid } from "../utils/jwt";

import { useQuery } from "@apollo/client";
import { GET_CURRENT_USER_QUERY } from "../../graphql/queries";

interface User {
  name: string;
  email: string;
  onboarded: boolean;
  [key: string]: any;
}

interface AuthContextType {
  token: any;
  isAuthenticated: boolean;
  user: User | null;
  login: (data: any) => Promise<void>;
  logout: () => void;
  setUser: (data: User) => Promise<void>;
  refetchUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const [token, setToken] = useLocalStorage("token", "");
  const navigate = useNavigate();
  const [user, setUserState] = React.useState<User | null>(null);

  // Apollo: fetch user if token is present
  const { data, refetch } = useQuery(GET_CURRENT_USER_QUERY, {
    skip: !token,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.getCurrentUser) {
        setUserState(data.getCurrentUser);
      } else {
        setUserState(null);
      }
    },
    onError: () => setUserState(null),
  });

  // Auto-logout if token is invalid
  useEffect(() => {
    if (token && !isTokenValid(token)) {
      setToken("");
      setUserState(null);
      navigate("/login", { replace: true });
    }
  }, [token, setToken, navigate]);

  // After login, fetch user and redirect based on onboarding status
  const login = async (data: any) => {
    setToken(data);
    const result = await refetch();
    const user = result?.data?.getCurrentUser;
    const onboarded = user?.onboarded;
    if (onboarded === false) {
      navigate("/onboarding", { replace: true });
    } else {
      navigate("/home", { replace: true });
    }
  };

  // Set user in both state and redux
  const setUser = async (user: User) => {
    setUserState(user);
    dispatch({ type: "SET_USER_DETAILS", payload: user });
  };

  // Expose refetch for onboarding completion
  const refetchUser = async () => {
    const result = await refetch();
    if (result?.data?.getCurrentUser) {
      setUserState(result.data.getCurrentUser);
    }
    return result;
  };

  const logout = () => {
    setToken("");
    setUserState(null);
    navigate("/", { replace: true });
  };

  const value = useMemo(
    () => ({
      token,
      isAuthenticated: !!token && isTokenValid(token) && !!user,
      user,
      login,
      logout,
      setUser,
      refetchUser,
    }),
    [login, logout, token, setUser, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
